import { useQuery } from '@tanstack/react-query';
import { FolderKanban, GraduationCap, Landmark, MapPin, UserRound } from 'lucide-react';
import { FIRST_PAGE } from '@/api/pagination';
import type { PillField } from '@/components/common/LabeledPill';
import { activeExamBoardsQueryOptions } from '@/features/examBoards/api/examBoards.api';
import { allDistrictListQueryOptions } from '@/features/geo/api/geo.api';
import { userDetailQueryOptions } from '@/features/users/api/users.api';
import { questionClassesQueryOptions, questionProjectsQueryOptions } from '../api/question.api';
import { OTHER_MENU_CLASS_SUFFIX, PROJECT_LOOKUP_PAGE_SIZE } from '../constants/question.constants';
import type { MonitorSearch } from '../types/question.types';

const toClassLabel = (className: string | undefined, isTpOtherMenu: boolean | undefined) => {
  if (className === undefined) return undefined;
  return isTpOtherMenu ? `${className}${OTHER_MENU_CLASS_SUFFIX}` : className;
};

export function useMonitorContextFields({
  projectId,
  boardId,
  classId,
  isTpOtherMenu,
  zillaId,
  userId,
}: MonitorSearch): PillField[] {
  const projects = useQuery({
    ...questionProjectsQueryOptions({ page: FIRST_PAGE, pageSize: PROJECT_LOOKUP_PAGE_SIZE }),
    enabled: projectId !== undefined,
  });
  const boards = useQuery({ ...activeExamBoardsQueryOptions(), enabled: boardId !== undefined });
  const classes = useQuery({ ...questionClassesQueryOptions(), enabled: classId !== undefined });
  const districts = useQuery({ ...allDistrictListQueryOptions(), enabled: zillaId !== undefined });
  const member = useQuery({
    ...userDetailQueryOptions(String(userId ?? 0)),
    enabled: userId !== undefined,
  });

  const fields: (PillField | null)[] = [
    projectId === undefined
      ? null
      : {
          key: 'project',
          icon: FolderKanban,
          label: 'Project',
          value: projects.data?.data.find((project) => project.projectId === projectId)?.name,
          isLoading: projects.isLoading,
        },
    boardId === undefined
      ? null
      : {
          key: 'board',
          icon: Landmark,
          label: 'Exam Board',
          value: boards.data?.find((board) => board.id === boardId)?.name,
          isLoading: boards.isLoading,
        },
    classId === undefined
      ? null
      : {
          key: 'class',
          icon: GraduationCap,
          label: 'Class',
          value: toClassLabel(
            classes.data?.find((questionClass) => questionClass.id === classId)?.name,
            isTpOtherMenu,
          ),
          isLoading: classes.isLoading,
        },
    zillaId === undefined
      ? null
      : {
          key: 'district',
          icon: MapPin,
          label: 'District',
          value: districts.data?.find((district) => district.id === zillaId)?.name,
          isLoading: districts.isLoading,
        },
    userId === undefined
      ? null
      : {
          key: 'member',
          icon: UserRound,
          label: 'ZM',
          value: member.data?.fullName,
          isLoading: member.isLoading,
        },
  ];

  return fields.filter((field) => field !== null);
}
